// import logo from './logo.svg';
import "./App.css";
import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
// import Overview from "./components/Overview";
import { observer } from "mobx-react";
import { ThemeProvider } from "@mui/material/styles";
// Master tables now accessed via react-query hooks (no context)
import { PatientsProvider } from "./stores/Patients/patients.provider";
import { AuthContext, useAuth } from "./stores/Auth/auth.provider";
import useStore from "./helpers/useStore";

import AppBarOverView from "./components/MenuBars/Appbar";
import { DoctorProvider } from "./stores/Doctor/doctor.provider";
import LoginForm from "./components/Login";
import { RxStoreProvider } from "./stores/Rx/rx.provider";
import { AppointmentProvider } from "./stores/Appointment/appointment.provider";
import { mainAppGridStyle, mobiliTheme } from "./themes/mobiliTheme";
import { NotesProvider } from "./stores/Notes/notes.provider";
import Scheduler from "./components/TerminPlan/Scheduler";
import MobiliCalendarPage from "./components/MobiliCalendar/MobiliCalendarPage";
import { CalendarProvider } from "./stores/Calendar/calendar.provider";
import Administration from "./components/Admin/Admin";
import SnackbarComponent from "./molecules/Notify";
import LoadingSpinner from "./atoms/LoadingSpinner";
import Dashboard from "./components/Dashboard/Dashboard";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de } from "date-fns/locale";
import ContinuousScheduler from "./components/TerminPlan/ContinuousScheduler";
import TransactionPage from "./components/Transactions/Base/pages/TransactionPage";
import { TransactionsProvider } from "./stores/Transactions/Transactions.provider";
import ResetLoginDetails from "./components/ResetLoginDetails";
import { Grid } from "@mui/material";
import { TicketsStoreProvider } from "./stores/Ticketing/tickets.provider";
import PatientLayout from "./components/PatientLayout/PatientLayout";
import History from "./components/History/History";
import { HistoryProvider } from "./stores/History/history.provider";
import { WebSocketContext } from "./stores/WebSocket/WebSocket.provider";
import StorageProvider from "./stores/Storage/Storage.provider";
import { BIDashboardPage } from "./components/BIDashboard/BIDashboard";
import { BIProvider } from "./stores/BIDashboard/biDashboard.provider";
import { DoctorManagement } from "./components/DoctorManagement/DoctorManagement";
import Registry from "./components/Registry/Registry";
import { RegistryProvider } from "./stores/Registry/registry.provider";
import InboxPage from "./components/Inbox/InboxPage";
import SalesPageMain from "./components/Sales/SaleMainMenu/SalePageMain";
import { SalesStoreProvider } from "./stores/Sales/sales.provider";
import { NoitceProvider } from "./stores/Notice/notice.provider";
// axiosConfig is imported in index.tsx (must be first to register interceptors)
import { AddressProvider } from "./stores/Address/address.provider";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import StoreDebugWindow from "./components/Debug/StoreDebugWindow";
import { Calendar } from "@fullcalendar/core";
import { TaxationProvider } from "./stores/Taxation/taxation.provider";
import { HotkeysProvider, useHotkeys, HotkeysHelp } from "./hotkeys";
import { useNavigationHotkeys } from "./hotkeys/hooks";
import PatientNavigationPane from "./components/NavigationPanes/PatientNavigationPane";
import RxNavigationPane from "./components/NavigationPanes/RxNavigationPane";
import { NavTablesProvider } from "./stores/NavTables";
import { AgentChatProvider } from "./stores/AgentChat/agentChat.provider";
import { MutationDraftProvider } from "./stores/MutationDraft/mutationDraft.provider";
import MutationSocketBridge from "./components/TerminPlan/SchedulerAgent/MutationSocketBridge";
import RxValidationSocketBridge from "./components/Registry/RxValidationSocketBridge";
import ChatWidget from "./components/AgentChat/ChatWidget";

//const theme = mobili
const App = observer(() => {
  const AuthStore = useStore(AuthContext);
  const { authToken, isBootstrapping } = AuthStore;

  // Compute directly from authToken - no async useEffect delay
  const isAuthenticated = !!authToken;

  const WebSocketStore = useStore(WebSocketContext);
  const { initializeWebSocket } = WebSocketStore;

  useEffect(() => {
    // Initialize the WebSocket connection
    initializeWebSocket();
  }, []);

  useEffect(() => {
    const baseTitle = "Mobili Decision";
    const instanceName = process.env.REACT_APP_INSTANCE_NAME;
    // Mirror the Appbar badge behaviour: only surface the instance name when it
    // is set and not the production instance, so users can tell which instance
    // (develop/release/staging/training) they're looking at.
    document.title =
      instanceName && instanceName !== "production" ? `${baseTitle} - ${instanceName}` : baseTitle;
  }, []);

  // While restoring the session from the refresh cookie, avoid flashing the
  // login screen or prematurely redirecting.
  if (isBootstrapping) {
    return <LoadingSpinner message="Anmeldung wird geprüft..." overlay />;
  }

  // useEffect(() => {
  //   const fetchData = async () => {
  //     setIsAuthenticated(!!authToken);
  //   };
  //   fetchData();
  // }, [authToken]);

  return (
    <TicketsStoreProvider>
      <TransactionsProvider>
        <PatientsProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <CalendarProvider>
              <RegistryProvider>
                <MutationDraftProvider>
                  <AgentChatProvider>
                    <HotkeysProvider>
                      <ThemeProvider theme={mobiliTheme}>
                        <NavTablesProvider>
                          <MutationSocketBridge />
                          <RxValidationSocketBridge />
                          <AppWithHotkeys isAuthenticated={isAuthenticated} />
                        </NavTablesProvider>
                      </ThemeProvider>
                    </HotkeysProvider>
                  </AgentChatProvider>
                </MutationDraftProvider>
              </RegistryProvider>
            </CalendarProvider>
          </LocalizationProvider>
        </PatientsProvider>
      </TransactionsProvider>
      {process.env.REACT_APP_TANSTACK_DEVTOOLS === "true" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </TicketsStoreProvider>
  );
});

// Separate component to use hotkeys context
const AppWithHotkeys: React.FC<{ isAuthenticated: boolean }> = ({ isAuthenticated }) => {
  const { isHelpOpen, setHelpOpen } = useHotkeys();

  // Register global navigation hotkeys
  useNavigationHotkeys("global");

  return (
    <>
      <SnackbarComponent />
      <StoreDebugWindow />
      <div className="App">
        <Grid
          container
          sx={{
            height: "100vh",
            width: "100vw",
            border: "0px dotted red",
            flexWrap: "nowrap",
            ...mainAppGridStyle,
          }}
        >
          {/* AppBar always visible for logo/name; auth-dependent items hidden inside */}
          <Grid
            item
            xs={0}
            md={isAuthenticated ? 0.28 : 0}
            xl={isAuthenticated ? 0.28 : 0}
            sx={{ border: "0px solid red", paddingTop: 9 }}
          >
            <AppBarOverView />
          </Grid>
          <Grid
            item
            md={isAuthenticated ? 12 - 0.28 : 12}
            xl={isAuthenticated ? 12 - 0.28 : 12}
            sx={{
              border: "0px dashed blue",
              paddingTop: 9,
              ...(isAuthenticated
                ? {}
                : {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    /**
                     * MD-339: the routed page keeps the full width of this area.
                     *
                     * A flex item sizes to its content, so a logged-out page
                     * that does not declare its own width collapsed to
                     * min-content — which squeezed the fields on
                     * /reset-password down to a few millimetres when opened
                     * from an invitation link. Login.tsx only escaped it by
                     * wrapping itself in a Container.
                     *
                     * Stretching the child rather than giving that one page a
                     * Container, because the reset forms lay themselves out
                     * with Grid columns keyed to VIEWPORT breakpoints
                     * (`md={6} lg={4}`): boxing them into a narrow parent makes
                     * those fractions resolve against the box instead of the
                     * screen and the fields end up narrow again. Pages that
                     * want to be narrow still cap themselves via maxWidth.
                     */
                    "& > *": { width: "100%" },
                  }),
            }}
          >
            <Routes>
              {!isAuthenticated && (
                <>
                  <Route
                    path="/login"
                    element={
                      <>
                        {/* <Navigate to="/login"  /> */}

                        <LoginForm />
                      </>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetLoginDetails forgotPassword={true} />}
                  />
                </>
              )}
              {isAuthenticated && (
                <>
                  <Route
                    path="/dashboard"
                    element={
                      <DoctorProvider>
                        <RxStoreProvider>
                          <AppointmentProvider>
                            <NotesProvider>
                              <Dashboard />
                            </NotesProvider>
                          </AppointmentProvider>
                        </RxStoreProvider>
                      </DoctorProvider>
                    }
                  />
                  <Route
                    path="/reset-password"
                    element={<ResetLoginDetails forgotPassword={true} />}
                  />
                  <Route
                    path="/patient"
                    element={
                      // <StepsProvider>
                      <DoctorProvider>
                        <RxStoreProvider>
                          <AppointmentProvider>
                            <NotesProvider>
                              <HistoryProvider>
                                <StorageProvider>
                                  <SalesStoreProvider>
                                    <NoitceProvider>
                                      <PatientLayout />
                                    </NoitceProvider>
                                  </SalesStoreProvider>
                                </StorageProvider>
                              </HistoryProvider>
                            </NotesProvider>
                          </AppointmentProvider>
                        </RxStoreProvider>
                      </DoctorProvider>
                      // </StepsProvider>
                    }
                  />
                  <Route
                    path="/transactions"
                    element={
                      <DoctorProvider>
                        <RxStoreProvider>
                          <HistoryProvider>
                            <AppointmentProvider>
                              <NotesProvider>
                                <TaxationProvider>
                                  <TransactionsProvider>
                                    <TransactionPage />
                                  </TransactionsProvider>
                                </TaxationProvider>
                              </NotesProvider>
                            </AppointmentProvider>
                          </HistoryProvider>
                        </RxStoreProvider>
                      </DoctorProvider>
                    }
                  />

                  <Route
                    path="/admin"
                    element={
                      <RxStoreProvider>
                        <Administration />
                      </RxStoreProvider>
                    }
                  />
                  <Route
                    path="/termin"
                    element={
                      <StorageProvider>
                        <AddressProvider>
                          <HistoryProvider>
                            <RxStoreProvider>
                              <AppointmentProvider>
                                <NotesProvider>
                                  <DoctorProvider>
                                    <Scheduler />
                                  </DoctorProvider>
                                </NotesProvider>
                              </AppointmentProvider>
                            </RxStoreProvider>
                          </HistoryProvider>
                        </AddressProvider>
                      </StorageProvider>
                    }
                  />
                  <Route
                    path="/mobilical"
                    element={
                      <StorageProvider>
                        <AddressProvider>
                          <HistoryProvider>
                            <RxStoreProvider>
                              <AppointmentProvider>
                                <NotesProvider>
                                  <DoctorProvider>
                                    <MobiliCalendarPage />
                                  </DoctorProvider>
                                </NotesProvider>
                              </AppointmentProvider>
                            </RxStoreProvider>
                          </HistoryProvider>
                        </AddressProvider>
                      </StorageProvider>
                    }
                  />
                  <Route
                    path="/continuous"
                    element={
                      <RxStoreProvider>
                        <AppointmentProvider>
                          <ContinuousScheduler />
                        </AppointmentProvider>
                      </RxStoreProvider>
                    }
                  />
                  <Route
                    path="/history"
                    element={
                      <RxStoreProvider>
                        <HistoryProvider>
                          <History />
                        </HistoryProvider>
                      </RxStoreProvider>
                    }
                  />
                  <Route
                    path="/biDashboard"
                    element={
                      <TransactionsProvider>
                        <BIProvider>
                          <BIDashboardPage />
                        </BIProvider>
                      </TransactionsProvider>
                    }
                  />

                  <Route
                    path="/sales"
                    element={
                      <HistoryProvider>
                        <RxStoreProvider>
                          <AppointmentProvider>
                            <SalesStoreProvider>
                              <SalesPageMain />
                            </SalesStoreProvider>
                          </AppointmentProvider>
                        </RxStoreProvider>
                      </HistoryProvider>
                    }
                  />
                  <Route
                    path="/doctor-management"
                    element={<Navigate to="/registry?tabSelection=doctors" replace />}
                  />
                  <Route
                    path="/registry"
                    element={
                      <DoctorProvider>
                        <RxStoreProvider>
                          <Registry />
                        </RxStoreProvider>
                      </DoctorProvider>
                    }
                  />
                  <Route path="/e-akte" element={<InboxPage />} />
                </>
              )}
              {/* Catch-all route: Redirect to /dashboard if authenticated, otherwise, to /login */}
              <Route
                path="*"
                element={isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />}
              />
            </Routes>
          </Grid>
        </Grid>
      </div>

      {/* Global help dialog */}
      <HotkeysHelp open={isHelpOpen} onClose={() => setHelpOpen(false)} />

      {/* Floating AI chat widget — visibility controlled inside the widget based on permissions */}
      {isAuthenticated && <ChatWidget />}
    </>
  );
};

export default App;
